import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Card, Table, Alert, Modal, Spinner } from 'react-bootstrap';
import { Contract, ContractSearch, ContractUpdate, TermOfPayment, AgreementMilestone } from '../types/contract';
import { getContract, updateContract, getAgreementMilestones, getContractsList } from '../services/api';
import { login } from '../services/auth';
import '../styles/ContractForm.scss';
import { ENVIRONMENT } from '../environments';
import Select from 'react-select';
//import { fetchContracts, fetchOffers } from '../services/searchService';
import { useNavigate } from 'react-router-dom';

interface ContractOption {
    value: string;
    label: string;
}

const ContractForm: React.FC = () => {
    const navigate = useNavigate();
    const [searchType, setSearchType] = useState<'contract' | 'offer'>('contract');
    const [searchParams, setSearchParams] = useState<ContractSearch>({
        CodCompany: '1',
        CodContract: '',
        CodSalesQuote: '',
        Version: '',
    });
    const [contract, setContract] = useState<Contract | null>(null);
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [loadingContracts, setLoadingContracts] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [newAmount, setNewAmount] = useState(0);
    const [description, setDescription] = useState('');
    const [fiadorNombre, setFiadorNombre] = useState('');
    const [fiadorCIF, setFiadorCIF] = useState('');
    const [fiadorEmail, setFiadorEmail] = useState('');
    const [clienteEmail, setClienteEmail] = useState('');
    const [comercialEmail, setComercialEmail] = useState('');
    const [milestones, setMilestones] = useState<AgreementMilestone[]>([]);
    const [apiResponse, setApiResponse] = useState<{ success: boolean } | null>(null);
    const [saving, setSaving] = useState(false);
    const [contractOptions, setContractOptions] = useState<ContractOption[]>([]);
    const [offerOptions, setOfferOptions] = useState<Array<{ value: string; label: string }>>([]);
    const [userDescription, setUserDescription] = useState<string>('');

    // Efecto para actualizar la descripción cuando cambie el importe
    useEffect(() => {
        if (contract && Math.abs(Number(newAmount) - Number(contract.ContractAmount)) > 0.01) {
            setDescription('Descuento de comercial');
        }
    }, [newAmount, contract]);

    /*useEffect(() => {
        const loadOptions = async () => {
            try {
                const contracts = await fetchContracts();
                const offers = await fetchOffers();
                
                setContractOptions(contracts.map(c => ({ value: c, label: c })));
                setOfferOptions(offers.map(o => ({ value: o, label: o })));
            } catch (error) {
                setError('Error al cargar las opciones de búsqueda');
            }
        };
        
        loadOptions();
    }, []);*/

    useEffect(() => {
        // Obtener la descripción del usuario del localStorage
        const userData = localStorage.getItem('userData');
        if (userData) {
            const { Description } = JSON.parse(userData);
            setUserDescription(Description);
        }
    }, []);

    useEffect(() => {
        const loadContracts = async () => {
            try {
                setLoadingContracts(true);
                setError('');

                // Obtener datos del usuario del localStorage
                const userData = localStorage.getItem('userData');
                if (!userData) {
                    setError('No se encontraron datos de usuario');
                    return;
                }

                const user = JSON.parse(userData);
                
                const response = await getContractsList(user.CodCompany, user.CodUser);
                if (!response.success) {
                    throw new Error(response.data as string || 'Error al obtener la lista de contratos');
                }

                // Transformar la respuesta en opciones para el Select
                const contracts = Array.isArray(response.data) ? response.data : [response.data];
                const options: ContractOption[] = contracts.map(contract => ({
                    value: contract.CodContract,
                    label: contract.TextShow || contract.CodContract // Fallback al código si no hay TextShow
                }));
                setContractOptions(options);
            } catch (err: any) {
                console.error('Error al cargar contratos:', err);
                setError(err.message || 'Error al cargar la lista de contratos');
            } finally {
                setLoadingContracts(false);
            }
        };

        loadContracts();
    }, []);

    const handleSearch = async () => {
        try {
            setLoading(true);
            setError('');
            setContract(null);
            
            // Validar que los campos requeridos estén llenos
            if (searchType === 'contract' && !searchParams.CodContract) {
                setError('Por favor ingrese el código del contrato');
                return;
            }
            if (searchType === 'offer' && (!searchParams.CodSalesQuote || !searchParams.Version)) {
                setError('Por favor ingrese el código de oferta y la versión');
                return;
            }

            console.log('Consultando contrato con:', searchParams);

            // Realizar login antes de la consulta
            try {
                await login();
            } catch (loginError: any) {
                setError('Error de autenticación: ' + (loginError.data || 'No se pudo iniciar sesión'));
                return;
            }

            // Obtener los hitos
            const milestonesResponse = await getAgreementMilestones();
            if (milestonesResponse.success) {
                setMilestones(milestonesResponse.data as AgreementMilestone[]);
            }

            // Log de la URL de la API
            // @ts-ignore
            if (window.getApiUrl) console.log('URL de la API usada:', window.getApiUrl());

            const response = await getContract(searchParams);
            console.log('Respuesta de getContract:', response);

            if (!response.success) {
                setError(typeof response.data === 'string' ? response.data : 'Error al consultar el contrato');
                return;
            }

            if (!response.data || typeof response.data === 'string') {
                setError('No se encontraron datos del contrato');
                return;
            }

            setContract(response.data as Contract);
            setNewAmount((response.data as Contract).ContractNewAmount || 0);
            setFiadorNombre((response.data as Contract).FiadorNombre || '');
            setFiadorCIF((response.data as Contract).FiadorCIF || '');
            setFiadorEmail((response.data as Contract).FiadorEmail || '');
            setClienteEmail((response.data as Contract).ClienteEmail || '');
            setComercialEmail((response.data as Contract).ComercialEmail || '');
            setIsEditing(false);
        } catch (err: any) {
            console.error('Error en la búsqueda:', err);
            setError(err.message || 'Error al buscar el contrato. Por favor intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        if (!contract) return;
        try {
            setError('');
            setLoading(true);
            setSaving(true);
            setApiResponse(null);

            // Validar que la suma de los porcentajes sea 100
            const totalPercent = contract.TermsOfPayments.reduce((sum, term) => sum + Number(term.Percent), 0);
            if (Math.abs(totalPercent - 100) > 0.01) { // Permitimos un pequeño margen de error por redondeo
                setError(`La suma de los porcentajes debe ser 100%. Actual: ${totalPercent.toFixed(2)}%`);
                setLoading(false);
                setSaving(false);
                return;
            }

            // Validar que si el importe ha cambiado, se haya especificado una descripción
            if (Math.abs(Number(newAmount) - Number(contract.ContractAmount)) > 0.01) {
                if (!description.trim()) {
                    setError('Si se ha modificado el importe se ha de especificar la descripción para el artículo usado');
                    setLoading(false);
                    setSaving(false);
                    return;
                }
            }

            const updateData: ContractUpdate = {
                CodCompany: contract.CodCompany,
                IDContract: contract.IDContract,
                ContractProjectType: contract.ContractProjectType,
                ContractNewAmount: newAmount,
                Description: description,
                Send: false,
                FiadorNombre: fiadorNombre,
                FiadorCIF: fiadorCIF,
                FiadorEmail: fiadorEmail,
                ClienteEmail: clienteEmail,
                ComercialEmail: comercialEmail,
                TermsOfPayments: contract.TermsOfPayments.map(term => ({
                    ...term,
                    IDTermOfPayment: term.IDTermOfPayment || null,
                    Comment: term.Comment || ''
                }))
            };

            const response = await updateContract(updateData);
            setApiResponse(response);
            // Extraer el mensaje del objeto de respuesta
            const message = typeof response.data === 'object' && response.data !== null 
                ? (response.data as any).Message || JSON.stringify(response.data)
                : response.data;
            setError(message as string);
            setIsEditing(false);
        } catch (err: any) {
            console.error('Error en la actualización:', err);
            const errorMessage = err.response?.data?.Message || err.message || 'Error al actualizar el contrato';
            setError(errorMessage);
            setApiResponse({ success: false });
        } finally {
            setLoading(false);
            setSaving(false);
        }
    };

    const handleAddTerm = () => {
        if (!contract) return;
        const newTerm: TermOfPayment = {
            IDTermOfPayment: null,
            IDAgreementMilestone: '',
            Percent: 0,
            Date: new Date().toISOString().split('T')[0],
            Description: '',
            Comment: ''
        };
        const updatedTerms = [...contract.TermsOfPayments, newTerm].sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());
        setContract({
            ...contract,
            TermsOfPayments: updatedTerms
        });
    };

    const handleRemoveTerm = (index: number) => {
        if (!contract) return;
        const updatedTerms = [...contract.TermsOfPayments];
        updatedTerms.splice(index, 1);
        const sortedTerms = updatedTerms.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());
        setContract({
            ...contract,
            TermsOfPayments: sortedTerms
        });
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        navigate('/login');
    };

    return (
        <div className="contract-form-container">
            {/* Modal de loading */}
            <Modal show={saving} centered backdrop="static" keyboard={false}>
                <Modal.Body className="d-flex flex-column align-items-center justify-content-center p-4">
                    <Spinner animation="border" role="status" className="mb-3" style={{ width: '3rem', height: '3rem', color: '#0047bb' }}>
                        <span className="visually-hidden">Cargando...</span>
                    </Spinner>
                    <div style={{ fontWeight: 500, fontSize: '1.1rem', textAlign: 'center' }}>
                        Se están guardando los cambios, espere por favor...
                    </div>
                </Modal.Body>
            </Modal>

            {/* Sección de Búsqueda */}
            <Card className="form-card mb-4">
                <Card.Body className="p-4">
                    <Card.Title>
                        Búsqueda de Contrato{' '}
                        {ENVIRONMENT && ENVIRONMENT !== 'prod' && (
                            <span style={{ color: 'red', fontWeight: 600 }}>(
                                {ENVIRONMENT === 'dev' ? 'desarrollo' : ENVIRONMENT === 'pre' ? 'preproducción' : ENVIRONMENT}
                            )</span>
                        )}
                    </Card.Title>                   
                    <Form 
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSearch();
                        }}
                        className="search-form"
                    >
                        {searchType === 'contract' ? (
                            <Form.Group className="mb-3">
                                {loadingContracts ? (
                                    <div className="d-flex align-items-center">
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        <span>Cargando contratos...</span>
                                    </div>
                                ) : (
                                    <Select
                                        options={contractOptions}
                                        value={contractOptions.find(option => option.value === searchParams.CodContract)}
                                        onChange={(option) => {
                                            setSearchParams({ ...searchParams, CodContract: option?.value || '' });
                                            setError('');
                                        }}
                                        placeholder="Seleccione un contrato"
                                        isClearable
                                        isSearchable
                                    />
                                )}
                            </Form.Group>
                        ) : (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Código de Oferta</Form.Label>
                                        <Select
                                            options={offerOptions}
                                            value={offerOptions.find(option => option.value === searchParams.CodSalesQuote)}
                                            onChange={(option) => {
                                                setSearchParams({ ...searchParams, CodSalesQuote: option?.value || '' });
                                                setError('');
                                            }}
                                            placeholder="Seleccione una oferta"
                                            isClearable
                                            isSearchable
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Versión</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={searchParams.Version}
                                            onChange={e => {
                                                setSearchParams({ ...searchParams, Version: e.target.value });
                                                setError('');
                                            }}
                                            placeholder="Ingrese la versión"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                        <div className="d-flex justify-content-end">
                            <Button 
                                type="button"
                                variant="primary" 
                                className="px-4"
                                disabled={loading || loadingContracts}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleSearch();
                                }}
                            >
                                {loading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Buscando...
                                    </>
                                ) : (
                                    'Buscar Contrato'
                                )}
                            </Button>
                        </div>
                    </Form>
                    {error && (
                        <Alert 
                            variant={apiResponse?.success ? 'success' : 'danger'} 
                            className="mt-3 mb-0"
                            onClose={() => {
                                setError('');
                                setApiResponse(null);
                            }}
                            dismissible
                        >
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{error}</pre>
                        </Alert>
                    )}
                </Card.Body>
            </Card>

            {/* Sección de Datos del Contrato */}
            {loading ? (
                <Card>
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" role="status" className="mb-3">
                            <span className="visually-hidden">Cargando...</span>
                        </Spinner>
                        <p className="mb-0">Cargando detalles del contrato...</p>
                    </Card.Body>
                </Card>
            ) : contract && (
                <Card className="form-card">
                    <Card.Body className="p-4">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Card.Title className="mb-0">Datos del Contrato</Card.Title>
                            <Button 
                                variant="success" 
                                onClick={handleUpdate}
                                disabled={loading}
                            >
                                {loading ? 'Guardando...' : 'Guardar Cambios'}
                            </Button>
                        </div>

                        {/* Datos básicos del contrato */}
                        <Row className="g-3 mb-4">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Contrato</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={contract.CodContract}
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Fecha Contrato</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={contract.ContractDate.split('T')[0]}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Importe Contrato</Form.Label>
                                    <div className="input-group">
                                        <Form.Control
                                            type="number"
                                            value={Number(contract.ContractAmount).toFixed(2)}
                                            disabled
                                            step="0.01"
                                            min="0"
                                        />
                                        <span className="input-group-text">€</span>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Datos de la oferta */}
                        <Row className="g-3 mb-4">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Oferta</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={contract.CodSalesQuote}
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Versión Oferta</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={contract.Version}
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Tipo Proyecto</Form.Label>
                                    <Form.Select
                                        value={contract.ContractProjectType}
                                        onChange={(e) => {
                                            setContract({
                                                ...contract,
                                                ContractProjectType: Number(e.target.value),
                                                ContractDesProjectType: e.target.value === '0' ? 'Proyecto Al Consumo' : 'Proyecto Cerrado'
                                            });
                                        }}
                                    >
                                        <option value="0">Proyecto Al Consumo</option>
                                        <option value="1">Proyecto Cerrado</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Emails Comercial y Cliente */}
                        <Row className="g-3 mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Email Comercial</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={comercialEmail}
                                        disabled
                                        placeholder="Email del comercial"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Email Cliente</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={clienteEmail}
                                        onChange={e => setClienteEmail(e.target.value)}
                                        placeholder="Ingrese el email del cliente"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Modificar importe contrato */}
                        <h6 className="section-title section-primary">Modificar importe contrato</h6>
                        <Row className="g-3 mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Nuevo Importe</Form.Label>
                                    <div className="input-group">
                                        <Form.Control
                                            type="number"
                                            value={Number(newAmount).toFixed(2)}
                                            onChange={(e) => setNewAmount(Number(e.target.value))}
                                            step="0.01"
                                            min="0"
                                        />
                                        <span className="input-group-text">€</span>
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Descripción</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Ingrese una descripción"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Sección Fiador */}
                        <h6 className="section-title section-primary">Fiador</h6>
                        <Row className="g-3 mb-4">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Nombre</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={fiadorNombre}
                                        onChange={(e) => setFiadorNombre(e.target.value)}
                                        placeholder="Ingrese el nombre del fiador"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>CIF</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={fiadorCIF}
                                        onChange={(e) => setFiadorCIF(e.target.value)}
                                        placeholder="Ingrese el CIF del fiador"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={fiadorEmail}
                                        onChange={(e) => setFiadorEmail(e.target.value)}
                                        placeholder="Ingrese el email del fiador"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <h6 className="section-title section-primary">Condiciones de Pago</h6>
                        <div className="table-responsive">
                            <Table hover striped className="table-striped-custom">
                                <thead>
                                    <tr>
                                        <th>Hito</th>
                                        <th>Porcentaje</th>
                                        <th>Fecha</th>
                                        <th>Descripción</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contract.TermsOfPayments
                                        .slice()
                                        .sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime())
                                        .map((term, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <Form.Select 
                                                        value={term.IDAgreementMilestone}
                                                        onChange={(e) => {
                                                            const updatedTerms = [...contract.TermsOfPayments];
                                                            updatedTerms[index] = {
                                                                ...updatedTerms[index],
                                                                IDAgreementMilestone: e.target.value
                                                            };
                                                            const sortedTerms = updatedTerms.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());
                                                            setContract({
                                                                ...contract,
                                                                TermsOfPayments: sortedTerms
                                                            });
                                                        }}
                                                    >
                                                        <option value="">Seleccione un hito</option>
                                                        {milestones.map((milestone) => (
                                                            <option key={milestone.IDAgreementMilestone} value={milestone.IDAgreementMilestone}>
                                                                {milestone.Description}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="number"
                                                        value={Number(term.Percent).toFixed(2)}
                                                        onChange={(e) => {
                                                            const updatedTerms = [...contract.TermsOfPayments];
                                                            updatedTerms[index] = {
                                                                ...updatedTerms[index],
                                                                Percent: Number(e.target.value)
                                                            };
                                                            const sortedTerms = updatedTerms.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());
                                                            setContract({
                                                                ...contract,
                                                                TermsOfPayments: sortedTerms
                                                            });
                                                        }}
                                                        step="0.01"
                                                        min="0"
                                                        max="100"
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="date"
                                                        value={term.Date.split('T')[0]}
                                                        onChange={(e) => {
                                                            const updatedTerms = [...contract.TermsOfPayments];
                                                            updatedTerms[index] = {
                                                                ...updatedTerms[index],
                                                                Date: e.target.value
                                                            };
                                                            const sortedTerms = updatedTerms.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());
                                                            setContract({
                                                                ...contract,
                                                                TermsOfPayments: sortedTerms
                                                            });
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={term.Description}
                                                        onChange={(e) => {
                                                            const updatedTerms = [...contract.TermsOfPayments];
                                                            updatedTerms[index] = {
                                                                ...updatedTerms[index],
                                                                Description: e.target.value
                                                            };
                                                            const sortedTerms = updatedTerms.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime());
                                                            setContract({
                                                                ...contract,
                                                                TermsOfPayments: sortedTerms
                                                            });
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleRemoveTerm(index)}
                                                        className="d-flex align-items-center gap-1"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                        Eliminar
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </Table>
                        </div>
                        <div className="d-flex justify-content-end mt-3">
                            <Button 
                                variant="primary" 
                                onClick={handleAddTerm}
                                className="d-flex align-items-center gap-2"
                            >
                                <i className="bi bi-plus-circle"></i>
                                Añadir Hito
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default ContractForm; 